/**
 * Estrae i campi diagnostici grezzi da un errore del plugin RevenueCat
 * (@revenuecat/purchases-capacitor). Il bridge nativo rigetta con un oggetto che
 * — oltre a `message` — può esporre il codice errore RevenueCat (`code`), la sua
 * forma leggibile (`readableErrorCode`) e il messaggio nativo sottostante
 * (`underlyingErrorMessage`).
 *
 * Catturarli tutti permette al triage Sentry di distinguere una key/ambiente
 * errati (es. `INVALID_CREDENTIALS_ERROR` / `CONFIGURATION_ERROR`) da un guasto
 * transitorio dello store (billing non disponibile al cold start) senza dover
 * leggere il logcat del dispositivo. Vedi issue #1460 (CERALIA-97): il
 * `configure error` indicava solo "configure error" + message generico, troppo
 * povero per capire se la causa fosse la chiave o l'ambiente.
 *
 * Modulo puro (nessun import nativo) così da essere testabile senza il plugin.
 */
export interface RevenueCatErrorInfo {
  message?: string;
  /** Codice errore RevenueCat (numerico) o stringa, a seconda della piattaforma. */
  code?: string | number;
  /** Forma leggibile del codice (es. "INVALID_CREDENTIALS_ERROR"). */
  readableErrorCode?: string;
  /** Messaggio dell'errore nativo sottostante (Play Billing / StoreKit). */
  underlyingErrorMessage?: string;
}

export function extractRevenueCatErrorInfo(e: unknown): RevenueCatErrorInfo {
  if (typeof e === 'string') return { message: e };
  if (!e || typeof e !== 'object') return {};

  const err = e as Record<string, unknown>;
  const info: RevenueCatErrorInfo = {};

  if (typeof err.message === 'string') info.message = err.message;
  if (typeof err.code === 'string' || typeof err.code === 'number') info.code = err.code;
  if (typeof err.readableErrorCode === 'string') info.readableErrorCode = err.readableErrorCode;
  if (typeof err.underlyingErrorMessage === 'string') {
    info.underlyingErrorMessage = err.underlyingErrorMessage;
  }

  return info;
}
